import React, { useState, useEffect, useMemo, useRef } from 'react';
import Icon from '../../../../components/AppIcon';
import {
  setScheduledShift,
  createOverride,
  createRecurringRule,
  createShiftSet,
  deleteShiftSet,
  assignUsersToShiftSet,
  timeToMinutes,
} from '../../../../api/Attendance/scheduling';
import { FetchUsers } from '../../../../api/General/account';
import { useSelector } from 'react-redux';
import Button from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { formatErrorMessage, notifyError } from '../../../../utils/errors';
import { DateField, TimeField } from '../../../../components/dateFields';
import { useTranslation } from 'react-i18next';
import { describeScheduleSaveError, groupScheduleConflicts, isNoResponse } from '../scheduleSaveError';
import { ScheduleSaveErrorDialog } from './ScheduleSaveErrorDialog';
import { getGoverningRule } from '../governingRule';
import { GAP_CANCELLED, saveRuleConfirmingSplit, SPLIT_CANCELLED, useSplitConfirm } from '../splitConfirm';
import { isPastStart, useSchedulePreSave } from '../schedulePreSave';

// The form fields an edit's init fills from `editingShift` / the matched schedule; the recurrence ones are keyed
// `recurrenceRule.<field>` so a touched end date does not shield the days.
const RECURRENCE_KEYS = ['days', 'endDate', 'interval'];

// Applies `next` over `prev` except for the fields the user has touched since the drawer opened.
const mergeUntouched = (prev, next, touched) => {
  const merged = { ...prev };
  Object.keys(next).forEach((key) => {
    if (key === 'recurrenceRule') return;
    if (!touched.has(key)) merged[key] = next[key];
  });
  if (next.recurrenceRule) {
    const recurrence = { ...prev.recurrenceRule };
    RECURRENCE_KEYS.forEach((key) => {
      if (key in next.recurrenceRule && !touched.has(`recurrenceRule.${key}`)) recurrence[key] = next.recurrenceRule[key];
    });
    merged.recurrenceRule = recurrence;
  }
  return merged;
};

const CreateScheduleDrawer = ({ isOpen, onClose, onScheduleCreated, editingShift, templates, users, preSelectedUser }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogData, setErrorDialogData] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(''); // '' = all
  
  // Get current user's role and company from Redux store
  const userRole = useSelector((state) => state.user.role);
  const userCompanyId = useSelector((state) => state.user.companyId);
  const isManager = useSelector((state) => state.user.isManager);
  const [formData, setFormData] = useState({
    type: 'single', // 'single' or 'recurring'
    date: '',
    startTime: '',
    endTime: '',
    breakStartTime: '',
    breakEndTime: '',
    breakDuration: null,
    userIds: [], // Changed from userId to userIds for multi-select
    templateId: '',
    createNewTemplate: true,
    newTemplateName: '',
    newTemplateDescription: '',
    addRecurrence: false,
    recurrenceRule: {
      frequency: 'WEEKLY',
      days: [],
      interval: 1,
      endDate: ''
    },
    reason: ''
  });
  const [originalRuleId, setOriginalRuleId] = useState(null);
  const [originalTemplateName, setOriginalTemplateName] = useState('');
  const [originalTemplateDescription, setOriginalTemplateDescription] = useState('');
  const [originalSetId, setOriginalSetId] = useState(null);
  const { ask: askSplit, askGap, dialog: splitDialog } = useSplitConfirm();
  const { askPastStart, askName, dialogs: preSaveDialogs } = useSchedulePreSave();
  // Schedules renamed from the same-name dialog while this drawer is open. The page's list is only refreshed when the
  // drawer closes (see pendingRefreshRef), so a retry after a refusal must not ask about the old name again.
  const [renamedSchedules, setRenamedSchedules] = useState({});
  const knownTemplates = useMemo(() => (templates || []).map((tp) => (
    renamedSchedules[tp?.id] ? { ...tp, name: renamedSchedules[tp.id] } : tp
  )), [templates, renamedSchedules]);

  // The fields the user has changed since this drawer opened, and the `editingShift` the form was filled from.
  // The edit init below re-runs when the employee list (account/Users, ~0.45 s on dev) or the templates finish
  // loading; it used to rewrite EVERY field then, so whatever was typed in the first second was lost and the
  // save sent a mix of stored and typed values with a 200 (CO-953, sched-drawer-refill.spec.ts). Now it fills
  // everything once per opened schedule, and later runs only refresh the fields nobody has touched.
  const touchedRef = useRef(new Set());
  const filledForRef = useRef(null);
  const markTouched = (...keys) => keys.forEach((key) => touchedRef.current.add(key));

  // "Create new" and an existing schedule use the same title, description and date fields, and each keeps its own
  // values across the tick, so unticking goes back to the schedule on the form and never saves what was typed for
  // the new one onto it.
  const createNewSidesRef = useRef({ existing: null, typed: null });
  // The date the drawer put in the field itself, as opposed to one the user chose.
  const carriedDateRef = useRef('');
  // The schedule this drawer was opened on. Its rule is not the rule of another schedule picked from the list.
  const openedSetIdRef = useRef(null);
  const forgetOpenedSchedule = () => {
    createNewSidesRef.current = { existing: null, typed: null };
    carriedDateRef.current = '';
    openedSetIdRef.current = null;
  };

  // A failed save that may still have left a set (and maybe its rule) behind asks the page to refresh, but
  // only once the drawer closes: refreshing now changes `templates`, which re-runs the init effect below and
  // would overwrite what the user typed with `editingShift` while they are still fixing the form.
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      // Reopening (even on the same schedule) starts a fresh form.
      filledForRef.current = null;
      touchedRef.current = new Set();
      forgetOpenedSchedule();
      setRenamedSchedules({});
    }
    if (!isOpen && pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      onScheduleCreated?.();
    }
  }, [isOpen]);

  // When opening the drawer for a NEW schedule (not editing), ensure stale edit state is cleared.
  // Otherwise we can accidentally PUT-update an existing rule from a different shift set.
  useEffect(() => {
    if (isOpen && !editingShift) {
      filledForRef.current = null;
      touchedRef.current = new Set();
      forgetOpenedSchedule();
      setError(null);
      setErrorDialogOpen(false);
      setErrorDialogData(null);
      setOriginalRuleId(null);
      setOriginalTemplateName('');
      setOriginalTemplateDescription('');
      setOriginalSetId(null);
      setFormData({
        type: 'single', // 'single' or 'recurring'
        date: '',
        startTime: '',
        endTime: '',
        breakStartTime: '',
        breakEndTime: '',
        breakDuration: null,
        userIds: preSelectedUser ? [preSelectedUser] : [],
        templateId: '',
        createNewTemplate: true,
        newTemplateName: '',
        newTemplateDescription: '',
        addRecurrence: false,
        recurrenceRule: {
          frequency: 'WEEKLY',
          days: [],
          interval: 1,
          endDate: ''
        },
        reason: ''
      });
    }
  }, [isOpen, editingShift, preSelectedUser]);

  const minutesToTimeString = (mins) => {
    if (typeof mins !== 'number' || !Number.isFinite(mins)) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const parseRecurrenceDaysFromRRule = (rRule) => {
    if (typeof rRule !== 'string' || rRule.trim().length === 0) return [];
    const byDayMatch = rRule.match(/BYDAY=([^;]+)/i);
    if (!byDayMatch || !byDayMatch[1]) return [];
    return byDayMatch[1]
      .split(',')
      .map((s) => (s || '').trim().toUpperCase())
      .filter(Boolean);
  };

  const parseRecurrenceIntervalFromRRule = (rRule) => {
    if (typeof rRule !== 'string' || rRule.trim().length === 0) return 1;
    const intervalMatch = rRule.match(/INTERVAL=(\d+)/i);
    if (!intervalMatch) return 1;
    const val = parseInt(intervalMatch[1], 10);
    return Number.isFinite(val) && val > 0 ? val : 1;
  };

  const applyTemplateToForm = (template) => {
    if (!template) return;

    const rule = getGoverningRule(template?.recurringRules);
    const recurrenceDays = parseRecurrenceDaysFromRRule(rule?.rRule);
    const recurrenceInterval = parseRecurrenceIntervalFromRRule(rule?.rRule);
    const rawEnd = typeof rule?.endDateUtc === 'string' ? rule.endDateUtc.split('T')[0] : '';
    const recurrenceEndDate = rawEnd === '9999-12-31' ? '' : rawEnd;

    const assignedIds = Array.isArray(template?.assignedUserIds)
      ? template.assignedUserIds
      : (Array.isArray(template?.assignedUsers) ? template.assignedUsers.map(u => u?.id).filter(Boolean) : []);

    const selectedUsers = (assignedIds || [])
      .map((id) => {
        const found = availableUsers.find((au) => String(au?.id) === String(id));
        const fallback = template?.assignedUsers?.find((u) => String(u?.id) === String(id));
        // Display only: someone no longer in availableUsers is named, never shown by their user id.
        return found || (fallback ? { id: fallback.id, name: fallback.name || fallback.fullName || t('people.formerEmployee'), fullName: fallback.fullName } : { id, name: t('people.formerEmployee') });
      })
      .filter((u) => u != null);

    // The save writes the date to the picked schedule's rule, so the other schedule's date must not stay here, or
    // picking would move this one's start. A date the user chose stays, and so does the day clicked on the calendar's
    // plus button: that drawer was not opened on a schedule, and the day is the one the change starts from.
    const pickedOpenedSchedule = Number(template?.id) === Number(openedSetIdRef.current);
    const pickedStart = typeof rule?.startDateUtc === 'string' ? rule.startDateUtc.split('T')[0] : '';
    const nextCarriedDate = pickedOpenedSchedule ? (editingShift?.date || '') : pickedStart;
    const replaceDate = !!openedSetIdRef.current && formData.date === carriedDateRef.current;
    if (replaceDate) carriedDateRef.current = nextCarriedDate;

    // The user's own pick: a later init run must not put the opened schedule's values back over it.
    markTouched('date', 'createNewTemplate', 'templateId', 'newTemplateName', 'newTemplateDescription', 'startTime', 'endTime',
      'breakStartTime', 'breakEndTime', 'breakDuration', 'userIds', 'recurrenceRule.days', 'recurrenceRule.endDate',
      'recurrenceRule.interval');
    setFormData((prev) => ({
      ...prev,
      // Selecting a template implies we are using an existing template.
      createNewTemplate: false,
      templateId: template?.id?.toString() || '',
      // Populate editable title/description for the selected template.
      newTemplateName: template?.name || '',
      newTemplateDescription: template?.description || '',
      ...(replaceDate ? { date: nextCarriedDate } : {}),
      // Populate schedule fields from the template's first recurring rule (best-effort).
      startTime: rule ? minutesToTimeString(rule.startTimeOfDayMinutes) : prev.startTime,
      endTime: rule ? minutesToTimeString(rule.endTimeOfDayMinutes) : prev.endTime,
      breakStartTime: rule ? minutesToTimeString(rule.breakStartTimeOfDayMinutes) : prev.breakStartTime,
      breakEndTime: rule ? minutesToTimeString(rule.breakEndTimeOfDayMinutes) : prev.breakEndTime,
      breakDuration: (rule?.breakDurationMinutes != null && Number(rule.breakDurationMinutes) > 0) ? Number(rule.breakDurationMinutes) : prev.breakDuration,
      userIds: selectedUsers.length > 0 ? selectedUsers : prev.userIds,
      recurrenceRule: {
        ...prev.recurrenceRule,
        days: recurrenceDays.length > 0 ? recurrenceDays : prev.recurrenceRule.days,
        endDate: recurrenceEndDate,
        interval: recurrenceInterval,
      },
    }));

    setOriginalTemplateName(template?.name || '');
    setOriginalTemplateDescription(template?.description || '');
    setOriginalSetId(template?.id || null);
    // When selecting an existing template, treat its governing rule as the one we're editing.
    // This prevents accidentally creating duplicate rules for the same template.
    setOriginalRuleId(
      rule?.id || template?.recurringRuleId || template?.linkedRecurringRuleId || null
    );
  };

  const extractDetailedError = (err, usersSaved) => {
    const result = err?.errors ? err : (err?.response?.data?.errors ? err.response.data : err);
    const errors = Array.isArray(result?.errors) ? result.errors : [];

    const descriptions = errors
      .map((e) => (typeof e?.description === 'string' ? e.description : (typeof e?.Description === 'string' ? e.Description : '')))
      .filter((s) => s && s.trim().length > 0);

    // Conflicts can be attached as errors[].details.conflicts (camelCase) or Details.Conflicts.
    let conflicts = null;
    for (const e of errors) {
      const details = e?.details || e?.Details;
      const c = details?.conflicts || details?.Conflicts;
      if (Array.isArray(c) && c.length > 0) {
        conflicts = c;
        break;
      }
    }

    // The helper already prefers a specific server description; Error.cs boilerplate gives way to this.
    const summary = describeScheduleSaveError(err, t, availableUsers, t('CouldntSaveSchedule'), usersSaved);
    return {
      summary,
      descriptions,
      employees: groupScheduleConflicts(conflicts, availableUsers),
      raw: result,
    };
  };

  // Load users when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableUsers();
    }
  }, [isOpen, templates]);

  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      
      // Fetch all users for the company (backend handles role-based filtering)
      const usersResponse = await FetchUsers('Company', userCompanyId, false);
      
      if (usersResponse && usersResponse.payload) {
        // Map all users to our format (normalize department to string)
        const allUsers = usersResponse.payload.map(user => {
          const dept = typeof user?.departmentName === 'string'
            ? user.departmentName
            : (typeof user?.department === 'string' 
                ? user.department 
                : (user?.department?.name || user?.department?.title || ''));
          return {
            id: user.id,
            name: user.fullName || user.userName,
            fullName: user.fullName,
            department: dept,
          };
        });
        
        // Note: We don't filter users here anymore.
        // Users can be selected for single shifts regardless of template assignments.
        // Template conflict checking is done on the backend when creating/assigning templates.
        allUsers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAvailableUsers(allUsers);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      const msg = formatErrorMessage(err, t('FailedToLoadUsers'));
      setError(msg);
      notifyError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const departmentOptions = useMemo(() => {
    const depts = (availableUsers || [])
      .map(u => {
        const dept = typeof u?.department === 'string'
          ? u.department
          : (u?.department?.name || u?.department?.title || '');
        return (dept || '').trim();
      })
      .filter(Boolean);
    return Array.from(new Set(depts)).sort((a, b) => a.localeCompare(b));
  }, [availableUsers]);

  const filteredUserOptions = useMemo(() => {
    const selected = Array.isArray(formData?.userIds) ? formData.userIds : [];
    const filtered = !selectedDepartment
      ? (availableUsers || [])
      : (availableUsers || []).filter(u => {
          const dept = (typeof u?.department === 'string'
            ? u.department
            : (u?.department?.name || u?.department?.title || ''));
          return (dept || '') === selectedDepartment;
        });

    // Ensure selected users remain in the options even if department filter excludes them.
    const byId = new Map();
    for (const u of filtered) byId.set(String(u?.id), u);
    for (const u of selected) {
      const key = String(u?.id);
      if (!byId.has(key)) byId.set(key, u);
    }
    return Array.from(byId.values());
  }, [availableUsers, formData?.userIds, selectedDepartment]);

  useEffect(() => {
    if (editingShift) {
      // We want the drawer to default to "Create New Template" only when creating a NEW schedule.
      // - Create button: editingShift is null (handled in the other effect)
      // - Plus button: editingShift is usually a small draft object like { date: 'YYYY-MM-DD' }
      // - Editing a calendar item or template: editingShift contains IDs/origin/template metadata
      const isEditingExisting = !!(
        editingShift?.id ||
        editingShift?.shiftSetId ||
        editingShift?.recurringShiftRuleId ||
        editingShift?.origin === 'Rule' ||
        editingShift?.origin === 'RecurringRule' ||
        editingShift?.ruleName
      );
      const shouldCreateNewTemplate = !isEditingExisting;

      // Get all assigned users from the consolidated shift
      let selectedUsers = [];
      
      if (editingShift.assignedUsers && editingShift.assignedUsers.length > 0) {
        // Map assigned users from shift to available users
        selectedUsers = editingShift.assignedUsers
          .map(u => {
            const foundUser = availableUsers.find(au => au.id === u.id);
            // If user not in availableUsers, create a placeholder
            return foundUser || { id: u.id, name: u.name || u.fullName || t('people.formerEmployee'), fullName: u.fullName };
          })
          .filter(u => u != null);
      } else if (editingShift.userId) {
        // Fallback to single user
        const selectedUser = availableUsers.find(u => u.id === editingShift?.userId);
        if (selectedUser) {
          selectedUsers = [selectedUser];
        }
      }
      
      // Find matching template based on shiftSetId, recurringShiftRuleId, or linkedRecurringRuleId
      let matchingTemplate = templates?.find(t => 
        t.id === editingShift?.shiftSetId || 
        t.recurringRuleId === editingShift?.recurringShiftRuleId ||
        t.linkedRecurringRuleId === editingShift?.recurringShiftRuleId
      );
      
      // Determine if this is a recurring shift and extract recurrence info
      const isRecurring = editingShift?.origin === 'Rule' || editingShift?.origin === 'RecurringRule';
      let recurrenceDays = [];
      let recurrenceEndDate = '';
      let recurrenceInterval = 1;
      
      // Try to get recurrence info from the matching template's recurring rules.
      // When a template has multiple rules (e.g. after a split), pick the specific
      // rule that matches the occurrence being edited; the governing rule only as a fallback.
      if (matchingTemplate && matchingTemplate.recurringRules?.length > 0) {
        const rule = (editingShift?.recurringShiftRuleId
          ? matchingTemplate.recurringRules.find(r => r.id === editingShift.recurringShiftRuleId)
          : null) || getGoverningRule(matchingTemplate.recurringRules);
        // Parse days from rRule like "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;INTERVAL=1"
        if (rule?.rRule) {
          const byDayMatch = rule.rRule.match(/BYDAY=([^;]+)/);
          if (byDayMatch) {
            recurrenceDays = byDayMatch[1].split(',');
          }
          const intervalMatch = rule.rRule.match(/INTERVAL=(\d+)/);
          if (intervalMatch) {
            recurrenceInterval = parseInt(intervalMatch[1], 10);
          }
        }
        if (rule?.endDateUtc) {
          const raw = rule.endDateUtc.split('T')[0];
          // Treat sentinel (open-ended) dates as "no end date" in UI.
          recurrenceEndDate = raw === '9999-12-31' ? '' : raw;
        }
      }
      
      // First run for this schedule: fill every field. Later runs (lists finished loading): only the untouched ones.
      if (filledForRef.current !== editingShift) {
        filledForRef.current = editingShift;
        touchedRef.current = new Set();
        forgetOpenedSchedule();
        carriedDateRef.current = editingShift?.date || '';
      }
      const touched = touchedRef.current;
      setFormData((prev) => mergeUntouched(prev, {
        type: isRecurring ? 'recurring' : 'single',
        date: editingShift?.date || '',
        startTime: editingShift?.startTime || '',
        endTime: editingShift?.endTime || '',
        breakStartTime: editingShift?.breakStartTime || '',
        breakEndTime: editingShift?.breakEndTime || '',
        breakDuration: Number.isFinite(editingShift?.breakDurationMinutes) ? editingShift.breakDurationMinutes : null,
        userIds: selectedUsers,
        templateId: matchingTemplate?.id?.toString() || editingShift?.shiftSetId?.toString() || '',
        createNewTemplate: shouldCreateNewTemplate,
        // Pre-fill the template's own title/description (as picking it in the Select does), so the
        // fields aren't blank and an untouched title/description doesn't count as an edit on save
        // (which used to PUT the set and overwrite its description with '').
        ...(isEditingExisting ? {
          newTemplateName: matchingTemplate?.name || editingShift?.ruleName || '',
          newTemplateDescription: matchingTemplate?.description || '',
        } : {}),
        reason: editingShift?.reason || editingShift?.overrideReason || '',
        recurrenceRule: {
          days: recurrenceDays,
          endDate: recurrenceEndDate,
          interval: recurrenceInterval,
        },
      }, touched));
      // The schedule the save goes to follows the template field: once the user picked another one, that pick
      // (applyTemplateToForm) set these and they stay.
      if (touched.has('templateId')) return;
      // Only ever treat a recurring rule id as a rule id (never fall back to shift set id).
      setOriginalRuleId(
        editingShift?.recurringShiftRuleId ||
          matchingTemplate?.recurringRuleId ||
          matchingTemplate?.linkedRecurringRuleId ||
          getGoverningRule(matchingTemplate?.recurringRules)?.id ||
          null
      );
      setOriginalTemplateName(matchingTemplate?.name || editingShift?.ruleName || '');
      setOriginalTemplateDescription(matchingTemplate?.description || '');
      setOriginalSetId(matchingTemplate?.id || editingShift?.shiftSetId || null);
      openedSetIdRef.current = matchingTemplate?.id || editingShift?.shiftSetId || null;
    }
  }, [editingShift, availableUsers, templates]);

  const handleInputChange = (field, value) => {
    markTouched(field);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleCreateNew = (checked) => {
    markTouched('createNewTemplate', 'templateId', 'newTemplateName', 'newTemplateDescription', 'date');
    const sides = createNewSidesRef.current;
    const leaving = {
      templateId: formData.templateId,
      newTemplateName: formData.newTemplateName,
      newTemplateDescription: formData.newTemplateDescription,
      date: formData.date,
    };
    if (checked) {
      sides.existing = leaving;
      // A new schedule gets its own title. A past start writes past shifts for everyone on it, so it has to be
      // chosen: one the drawer carried over from the old schedule is not kept. HR can still pick a past date.
      const today = new Date().toLocaleDateString('en-CA');
      const carriedPast = !!formData.date && formData.date === carriedDateRef.current && formData.date < today;
      setFormData((prev) => ({
        ...prev,
        createNewTemplate: true,
        templateId: '',
        newTemplateName: sides.typed?.newTemplateName || '',
        newTemplateDescription: sides.typed?.newTemplateDescription || '',
        date: sides.typed ? sides.typed.date : (carriedPast ? '' : prev.date),
      }));
      return;
    }
    sides.typed = leaving;
    setFormData((prev) => ({
      ...prev,
      createNewTemplate: false,
      templateId: sides.existing?.templateId || '',
      newTemplateName: sides.existing?.newTemplateName || '',
      newTemplateDescription: sides.existing?.newTemplateDescription || '',
      date: sides.existing ? sides.existing.date : prev.date,
    }));
  };

  const usersInSelectedDepartment = useMemo(() => {
    const list = Array.isArray(availableUsers) ? availableUsers : [];
    if (!selectedDepartment) return list;
    return list.filter((u) => {
      const dept = (typeof u?.department === 'string'
        ? u.department
        : (u?.department?.name || u?.department?.title || ''));
      return (dept || '') === selectedDepartment;
    });
  }, [availableUsers, selectedDepartment]);

  const selectedUserIdsSet = useMemo(() => {
    const selected = Array.isArray(formData?.userIds) ? formData.userIds : [];
    return new Set(selected.map((u) => String(u?.id)));
  }, [formData?.userIds]);

  const usersInSelectedDepartmentIds = useMemo(() => {
    return usersInSelectedDepartment.map((u) => String(u?.id)).filter(Boolean);
  }, [usersInSelectedDepartment]);

  const allUsersSelectedInDepartment =
    usersInSelectedDepartmentIds.length > 0 &&
    usersInSelectedDepartmentIds.every((id) => selectedUserIdsSet.has(id));

  const someUsersSelectedInDepartment =
    usersInSelectedDepartmentIds.some((id) => selectedUserIdsSet.has(id)) &&
    !allUsersSelectedInDepartment;

  const toggleSelectAllUsersInDepartment = (checked) => {
    const selected = Array.isArray(formData?.userIds) ? formData.userIds : [];

    if (checked) {
      const byId = new Map();
      for (const u of selected) byId.set(String(u?.id), u);
      for (const u of usersInSelectedDepartment) byId.set(String(u?.id), u);
      handleInputChange('userIds', Array.from(byId.values()).filter((u) => u?.id != null));
      return;
    }

    const removeIds = new Set(usersInSelectedDepartmentIds);
    handleInputChange('userIds', selected.filter((u) => !removeIds.has(String(u?.id))));
  };

  const handleRecurrenceChange = (field, value) => {
    markTouched(`recurrenceRule.${field}`);
    setFormData(prev => ({
      ...prev,
      recurrenceRule: {
        ...prev?.recurrenceRule,
        [field]: value
      }
    }));
  };

  const toggleDay = (day) => {
    markTouched('recurrenceRule.days');
    setFormData(prev => ({
      ...prev,
      recurrenceRule: {
        ...prev?.recurrenceRule,
        days: prev?.recurrenceRule?.days?.includes(day)
          ? prev?.recurrenceRule?.days?.filter(d => d !== day)
          : [...prev?.recurrenceRule?.days, day]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    // CO-996: true only when the server asked for the employees to be written before the rule (see DeferredUsers in
    // splitConfirm.tsx) and they were, so the message can say what did and did not land. Any other refused save
    // changes nothing.
    let usersSaved = false;
    // 'unknown' when creating a new set or its rule got no answer (the server may still be saving),
    // 'cleanupFailed' when the empty set left by a refused rule could not be deleted.
    let createOutcome = null;
    let createdSetName = '';
    // CO-957: the user cancelled the split (or the gap, ruling A) the server announced; the rule was not saved.
    let splitCancelled = false;
    let gapCancelled = false;
    // CO-1021: the new name of another schedule, renamed from the same-name dialog before this save was sent.
    let otherRenamedTo = '';
    const otherRenamedNote = () => (otherRenamedTo ? ` ${t('ScheduleOtherRenamedNote', { name: otherRenamedTo })}` : '');
    // CO-996: an employee change on an existing schedule, sent with the rule instead of written to the schedule first.
    let deferredUsers;
    // The same for a changed title or description.
    const scheduleEdit = {};

    try {
      // Validate required fields
      if (!formData.userIds || formData.userIds.length === 0) {
        throw new Error(t('PleaseSelectAtLeastOneUser'));
      }
      if (!formData.date) {
        throw new Error(t('PleaseSelectDate'));
      }
      if (!formData.startTime || !formData.endTime) {
        throw new Error(t('PleaseSelectStartAndEndTimes'));
      }

      // Convert times to minutes
      const startTimeMinutes = timeToMinutes(formData.startTime);
      const endTimeMinutes = timeToMinutes(formData.endTime);
      const breakDurationMinutesRaw = (Number.isFinite(formData.breakDuration) && formData.breakDuration > 0)
        ? formData.breakDuration
        : null;
      const breakStartTimeMinutes = formData.breakStartTime ? timeToMinutes(formData.breakStartTime) : null;
      const breakEndTimeMinutes = formData.breakEndTime ? timeToMinutes(formData.breakEndTime) : null;

      // Validate break window (client-side mirror; backend remains source of truth)
      const overnight = endTimeMinutes <= startTimeMinutes;
      const shiftLen = overnight
        ? (24 * 60 - startTimeMinutes + endTimeMinutes)
        : (endTimeMinutes - startTimeMinutes);

      const toOffset = (minutes) => {
        let off = minutes - startTimeMinutes;
        if (overnight && off < 0) off += 24 * 60;
        return off;
      };

      const hasBreakConfig = breakStartTimeMinutes !== null || breakEndTimeMinutes !== null || breakDurationMinutesRaw !== null;
      if (hasBreakConfig) {
        const startOffset = (breakStartTimeMinutes !== null) ? toOffset(breakStartTimeMinutes) : null;
        const endOffset = (breakEndTimeMinutes !== null) ? toOffset(breakEndTimeMinutes) : null;

        if (startOffset !== null) {
          if ((!overnight && startOffset < 0) || startOffset < 0 || startOffset >= shiftLen) {
            throw new Error('Break start time must be within the shift');
          }
        }

        if (endOffset !== null) {
          if ((!overnight && endOffset < 0) || endOffset <= 0 || endOffset > shiftLen) {
            throw new Error('Break end time must be within the shift');
          }
        }

        if (breakDurationMinutesRaw !== null && breakDurationMinutesRaw > shiftLen) {
          throw new Error('Break duration must fit within the shift');
        }

        if (startOffset !== null && endOffset !== null) {
          if (endOffset <= startOffset) {
            throw new Error('Break end time must be after break start time');
          }
          const windowLen = endOffset - startOffset;
          if (breakDurationMinutesRaw !== null && breakDurationMinutesRaw > windowLen) {
            throw new Error('Break duration must not exceed break window length');
          }
        } else {
          if (breakDurationMinutesRaw !== null) {
            if (startOffset !== null && startOffset + breakDurationMinutesRaw > shiftLen) {
              throw new Error('Break start time and duration must fit within the shift');
            }
            if (endOffset !== null && breakDurationMinutesRaw > endOffset) {
              throw new Error('Break duration must fit within the shift before break end time');
            }
          }
        }
      }

      if (formData.type === 'single') {
        // Create single shift or override for each selected user
        for (const user of formData.userIds) {
          const effectiveId = (editingShift && String(user.id) === String(editingShift.userId))
            ? (editingShift.id || 0)
            : 0;

          const shiftInput = {
            id: effectiveId,
            userId: user.id,
            date: formData.date,
            startTimeMinutes,
            endTimeMinutes,
            breakStartTimeMinutes: breakStartTimeMinutes ?? undefined,
            breakEndTimeMinutes: breakEndTimeMinutes ?? undefined,
            breakDurationMinutes: breakDurationMinutesRaw ?? undefined,
            overrideReason: formData.reason || undefined,
          };

          if (editingShift?.isVirtual) {
            // Converting virtual to manual override
            await createOverride(shiftInput);
          } else {
            // Creating or updating manual shift
            await setScheduledShift(shiftInput);
          }
        }
      } else if (formData.type === 'recurring') {
        // Create recurring rule
        if (formData.recurrenceRule.days.length === 0) {
          throw new Error(t('PleaseSelectAtLeastOneDayForRecurrence'));
        }

        // A blank list has nothing to update: the save would go to a schedule the form does not show.
        if (!formData.createNewTemplate && !formData.templateId) {
          throw new Error(t('PleaseSelectRecurringScheduleForRecurringRule'));
        }

        if (formData.createNewTemplate) {
          if (!(formData.newTemplateName || '').trim()) {
            throw new Error(t('PleaseEnterScheduleTitle'));
          }
          // CO-1021 (owner: "Warn, let them save"). Nothing is refused, and any past start on a new schedule is said,
          // also a day clicked on the calendar: the effect is the same.
          if (isPastStart(formData.date) && !(await askPastStart(formData.date, formData.userIds.length))) return;
        }

        // CO-1021 (owner: "let them choose a different name for either of the two"). Two schedules with one name cannot
        // be told apart in the list or in a refusal. An existing schedule is only asked about a title being changed.
        const typedTitle = (formData.newTemplateName || '').trim();
        let scheduleTitle = formData.newTemplateName || '';
        if (typedTitle && (formData.createNewTemplate || typedTitle !== (originalTemplateName || '').trim())) {
          const named = await askName({
            name: typedTitle,
            schedules: knownTemplates,
            selfId: formData.createNewTemplate ? null : formData.templateId,
          });
          if (!named) return;
          if (named.name !== typedTitle) {
            scheduleTitle = named.name;
            markTouched('newTemplateName');
            setFormData((prev) => ({ ...prev, newTemplateName: named.name }));
          }
          if (named.renamedOther) {
            const { id: renamedId, name: renamedTo } = named.renamedOther;
            otherRenamedTo = renamedTo;
            pendingRefreshRef.current = true;
            setRenamedSchedules((prev) => ({ ...prev, [renamedId]: renamedTo }));
            // The other schedule may be the one this drawer holds behind the tick: unticking must show its new name.
            const sides = createNewSidesRef.current;
            if (sides.existing && Number(sides.existing.templateId) === Number(renamedId)) {
              sides.existing = { ...sides.existing, newTemplateName: renamedTo };
            }
            if (Number(originalSetId) === Number(renamedId)) setOriginalTemplateName(renamedTo);
          }
        }

        // Build RRULE string
        const rruleString = `FREQ=WEEKLY;BYDAY=${formData.recurrenceRule.days.join(',')};INTERVAL=${formData.recurrenceRule.interval || 1}`;

        const selectedUserIds = formData.userIds.map(u => u.id);

        // Determine / create the owning shift set
        // For create flow: rely on selected templateId.
        // For edit flow: fall back to the existing shiftSetId.
        let shiftSetId = 0;
        let newlyCreatedSetId = null; // Track for cleanup if rule creation fails
        if (formData.templateId) shiftSetId = Number(formData.templateId);
        else if (editingShift?.shiftSetId) shiftSetId = Number(editingShift.shiftSetId);
        else if (originalSetId) shiftSetId = Number(originalSetId);

        if (formData.createNewTemplate) {
          const setInput = {
            name: scheduleTitle,
            description: formData.newTemplateDescription || '',
            assignedUserIds: selectedUserIds,
            embeddedSingleShifts: [],
            ruleItems: [],
          };
          createdSetName = (setInput.name || '').trim();
          let createdSet;
          try {
            createdSet = await createShiftSet(setInput);
          } catch (setErr) {
            if (isNoResponse(setErr)) createOutcome = 'unknown';
            throw setErr;
          }
          shiftSetId = createdSet?.id;
          newlyCreatedSetId = shiftSetId;
        } else if (shiftSetId) {
          // CO-996 (owner, 2026-11-02): a refused save is all or nothing, so an edited title, description or employee
          // list goes with the rule, and only what changed is sent. A blank title keeps the current one. When the
          // page does not hold the schedule the list is sent anyway, and the server compares it.
          const nameChanged = scheduleTitle !== (originalTemplateName || '');
          const descChanged = (formData.newTemplateDescription || '') !== (originalTemplateDescription || '');
          const newName = scheduleTitle.trim();
          if (nameChanged && newName) scheduleEdit.scheduleName = newName;
          if (descChanged) scheduleEdit.scheduleDescription = formData.newTemplateDescription || '';

          const usersBefore = templates?.find((tp) => Number(tp?.id) === Number(shiftSetId))?.assignedUserIds;
          if (!Array.isArray(usersBefore)
            || JSON.stringify(usersBefore.map(String).sort()) !== JSON.stringify(selectedUserIds.map(String).sort())) {
            const setIdForUsers = shiftSetId;
            deferredUsers = {
              userIds: selectedUserIds,
              write: async () => {
                await assignUsersToShiftSet(setIdForUsers, selectedUserIds);
                usersSaved = true;
              },
            };
          }
        }

        if (!shiftSetId) {
          throw new Error(t('FailedToDetermineShiftSetForRecurringRule'));
        }

        // CO-1021: "create new" is always a new rule under the new set. The rule this drawer was opened on, or the one a
        // picked template brought, belongs to another set: the old schedule stays as it is.
        // Another schedule picked from the list is edited through its own rule, which the pick put in originalRuleId.
        const pickedAnotherSchedule = !!openedSetIdRef.current && Number(formData.templateId) !== Number(openedSetIdRef.current);
        const openedRuleId = pickedAnotherSchedule ? null : editingShift?.recurringShiftRuleId;
        const ruleIdToUpdate = formData.createNewTemplate ? 0 : (openedRuleId || originalRuleId || 0);
        const editingExistingRule = Number(ruleIdToUpdate) > 0;
        const ruleInput = {
          id: ruleIdToUpdate || 0,
          shiftSetId,
          // NOTE: timeZoneId is NOT sent - backend determines from shift set/company settings
          rRule: rruleString,
          startTimeOfDayMinutes: startTimeMinutes,
          endTimeOfDayMinutes: endTimeMinutes,
          breakStartTimeOfDayMinutes: breakStartTimeMinutes ?? undefined,
          breakEndTimeOfDayMinutes: breakEndTimeMinutes ?? undefined,
          breakDurationMinutes: breakDurationMinutesRaw ?? undefined,
          startDateUtc: formData.date,
          // No end date = null, which the server stores as open-ended (clearing the field removes it).
          endDateUtc: formData?.recurrenceRule?.endDate || null,
          // Opened from a calendar shift, the date is the occurrence the change starts from. Opened from the
          // schedule itself (Edit on its card), it is the schedule's start date: no effectiveDateUtc then, so a
          // schedule that has not started yet moves its start instead of splitting.
          ...(editingExistingRule && !editingShift?.fromTemplate && !pickedAnotherSchedule ? { effectiveDateUtc: formData.date } : {}),
          ...scheduleEdit,
        };

        // Only update when we are actually editing an existing recurring rule.
        // Creating a schedule should always POST a new rule for the selected shift set.
        try {
          if (editingExistingRule) {
            // CO-957: a change that would split the schedule is confirmed first (it ends this one and adds another).
            const saved = await saveRuleConfirmingSplit(Number(ruleIdToUpdate), ruleInput, { ask: askSplit, askGap }, deferredUsers);
            splitCancelled = saved === SPLIT_CANCELLED;
            gapCancelled = saved === GAP_CANCELLED;
          } else {
            await createRecurringRule(deferredUsers ? { ...ruleInput, assignedUserIds: deferredUsers.userIds } : ruleInput);
          }
        } catch (ruleErr) {
          // If we just created a new ShiftSet and the rule was refused (e.g. conflict),
          // clean up the orphaned ShiftSet so it doesn't persist without a rule. Not when the
          // rule save got no answer: the server may still be creating it, and deleting the set
          // now could leave that rule linked to an archived set.
          if (newlyCreatedSetId) {
            if (isNoResponse(ruleErr)) {
              createOutcome = 'unknown';
            } else {
              try { await deleteShiftSet(newlyCreatedSetId, { cascade: true }); } catch { createOutcome = 'cleanupFailed'; }
            }
          }
          throw ruleErr;
        }

        if (splitCancelled || gapCancelled) {
          // The drawer stays open with what the user typed. Employees the server asked to be written first are said,
          // and the page refreshes when the drawer closes (refreshing now would re-run the init above).
          const savedNote = usersSaved ? ` ${t('ScheduleRuleNotSavedUsersSaved')}` : '';
          if (usersSaved) pendingRefreshRef.current = true;
          setError(`${t(gapCancelled ? 'ScheduleGapCancelled' : 'ScheduleSplitCancelled')}${savedNote}${otherRenamedNote()}`);
          return;
        }
      }

      // Success - close drawer and refresh data (this refresh also covers any deferred one)
      pendingRefreshRef.current = false;
      onScheduleCreated?.();
      onClose?.();
      
      // Reset form
      setFormData({
        type: 'single',
        date: '',
        startTime: '',
        endTime: '',
        breakStartTime: '',
        breakEndTime: '',
        breakDuration: null,
        userIds: [],
        templateId: '',
        createNewTemplate: true,
        newTemplateName: '',
        newTemplateDescription: '',
        addRecurrence: false,
        recurrenceRule: {
          frequency: 'WEEKLY',
          days: [],
          interval: 1,
          endDate: ''
        },
        reason: ''
      });

      // Reset edit linkage state as well
      setOriginalRuleId(null);
      setOriginalTemplateName('');
      setOriginalSetId(null);
    } catch (err) {
      console.error('Error creating schedule:', err);
      const savedNote = usersSaved ? ` ${t('ScheduleRuleNotSavedUsersSaved')}` : '';
      const cleanupNote = createOutcome === 'cleanupFailed'
        ? ` ${t('ScheduleEmptySetMayRemain', { name: createdSetName })}`
        : '';
      // A set (and maybe its rule) or a new employee list may now exist that the page has not loaded: refresh its
      // lists when the drawer closes (see pendingRefreshRef), not now while the user is still on the form.
      if (createOutcome || usersSaved) pendingRefreshRef.current = true;
      const msg = createOutcome === 'unknown'
        ? t('ScheduleSaveOutcomeUnknown')
        : describeScheduleSaveError(err, t, availableUsers, t('FailedToCreateScheduleTryAgain'), usersSaved) + savedNote + cleanupNote + otherRenamedNote();
      setError(msg);

      // Show a detailed dialog with backend error(s) + conflict examples (if provided). Not for an
      // unanswered request: there are no details, and the inline message already says what to do.
      if (createOutcome !== 'unknown') {
        try {
          const details = extractDetailedError(err, usersSaved);
          if (savedNote || cleanupNote || otherRenamedTo) details.summary = `${details.summary}${savedNote}${cleanupNote}${otherRenamedNote()}`;
          setErrorDialogData(details);
          setErrorDialogOpen(true);
        } catch {
          // ignore dialog parsing failures
        }
      }

      notifyError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const weekDays = [
    { short: 'MO', full: t('Monday') },
    { short: 'TU', full: t('Tuesday') },
    { short: 'WE', full: t('Wednesday') },
    { short: 'TH', full: t('Thursday') },
    { short: 'FR', full: t('Friday') },
    { short: 'SA', full: t('Saturday') },
    { short: 'SU', full: t('Sunday') }
  ];

  const startTimeMinutesPreview = formData?.startTime ? timeToMinutes(formData.startTime) : null;
  const endTimeMinutesPreview = formData?.endTime ? timeToMinutes(formData.endTime) : null;
  const isOvernight =
    typeof startTimeMinutesPreview === 'number' &&
    typeof endTimeMinutesPreview === 'number' &&
    endTimeMinutesPreview <= startTimeMinutesPreview;
  const durationMinutes =
    typeof startTimeMinutesPreview === 'number' && typeof endTimeMinutesPreview === 'number'
      ? ((endTimeMinutesPreview - startTimeMinutesPreview + (isOvernight ? 1440 : 0)))
      : null;
  const durationLabel =
    typeof durationMinutes === 'number'
      ? t('DurationHoursMinutes', {
          hours: Math.floor(durationMinutes / 60),
          minutes: durationMinutes % 60,
        })
      : null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="absolute inset-y-0 max-w-2xl w-full bg-card shadow-xl" style={{ insetInlineEnd: 0 }}>
        <div className="h-full flex flex-col">
          <ScheduleSaveErrorDialog open={!!errorDialogOpen} details={errorDialogData} onClose={() => setErrorDialogOpen(false)} />

          {splitDialog}

          {preSaveDialogs}

          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              {editingShift ? t('EditScheduledShift') : t('CreateScheduledShift')}
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Form Content */}
          <form id="create-schedule-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-destructive" />
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                </div>
              )}
              {/* Shift Type Selection */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('ShiftType')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('type', 'single')}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      formData?.type === 'single' ?'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-border'
                    }`}
                  >
                    <Icon name="Calendar" size={24} className="mx-auto mb-2" />
                    <div className="font-medium">{t('SingleShift')}</div>
                    <div className="text-xs mt-1 opacity-75">{t('OneTimeOccurrence')}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('type', 'recurring')}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      formData?.type === 'recurring' ?'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-border'
                    }`}
                  >
                    <Icon name="Repeat" size={24} className="mx-auto mb-2" />
                    <div className="font-medium">{t('RecurringShift')}</div>
                    <div className="text-xs mt-1 opacity-75">{t('RepeatingPattern')}</div>
                  </button>
                </div>
              </div>

              {/* Recurring Schedule (recurring only) */}
              {formData?.type === 'recurring' && (
                <div className="p-4 bg-muted rounded-lg space-y-4">
                  <h3 className="font-medium text-foreground flex items-center">
                    <Icon name="Grid" size={18} className="me-2" />
                    {t('RecurringSchedule')}
                  </h3>

                  <Checkbox
                    checked={!!formData?.createNewTemplate}
                    onChange={(e) => toggleCreateNew(!!e?.target?.checked)}
                    label={t('CreateNewRecurringSchedule')}
                  />

                  {formData?.createNewTemplate ? (
                    <div className="space-y-3">
                      <Input
                        label={t('ScheduleTitle')}
                        required
                        placeholder={t('ScheduleTitle')}
                        value={formData?.newTemplateName}
                        onChange={(e) => handleInputChange('newTemplateName', e?.target?.value)}
                      />
                      <textarea
                        placeholder={t('ScheduleDescription')}
                        value={formData?.newTemplateDescription}
                        onChange={(e) => handleInputChange('newTemplateDescription', e?.target?.value)}
                        rows="2"
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Select
                        label={t('RecurringSchedule')}
                        required
                        placeholder={t('SelectRecurringSchedule', { defaultValue: 'Select recurring schedule' })}
                        options={[
                          ...knownTemplates.map((template) => ({
                            label: template?.name,
                            value: String(template?.id),
                          })),
                        ]}
                        value={formData?.templateId || ''}
                        onChange={(val) => {
                          handleInputChange('templateId', val);
                          const selected = knownTemplates.find((tmp) => String(tmp?.id) === String(val));
                          if (selected) applyTemplateToForm(selected);
                        }}
                      />

                      {/* Editable title/description for the selected template */}
                      <Input
                        label={t('ScheduleTitle')}
                        required
                        placeholder={t('ScheduleTitle')}
                        value={formData?.newTemplateName}
                        onChange={(e) => handleInputChange('newTemplateName', e?.target?.value)}
                        disabled={!formData?.templateId}
                      />
                      <textarea
                        placeholder={t('ScheduleDescription')}
                        value={formData?.newTemplateDescription}
                        onChange={(e) => handleInputChange('newTemplateDescription', e?.target?.value)}
                        rows="2"
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!formData?.templateId}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Date Selection */}
              <div className={"grid gap-4 " + (formData?.type === 'recurring' ? 'grid-cols-2' : 'grid-cols-1')}>
                <DateField
                  label={formData?.type === 'single' ? t('Date') : t('StartDate')}
                  required
                  value={formData?.date}
                  onChange={(v) => handleInputChange('date', v)}
                />
                {/* End Date */}
                {formData?.type === 'recurring' && (
                  <DateField
                    label={t('EndDateOptional')}
                    value={formData?.recurrenceRule?.endDate}
                    onChange={(v) => handleRecurrenceChange('endDate', v)}
                  />
                )}
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <TimeField
                  label={t('StartTime')}
                  required
                  value={formData?.startTime}
                  onChange={(v) => handleInputChange('startTime', v)}
                />
                <div>
                  <TimeField
                    label={t('EndTime')}
                    required
                    value={formData?.endTime}
                    onChange={(v) => handleInputChange('endTime', v)}
                  />
                  {(isOvernight || durationLabel) && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {isOvernight ? t('OvernightShiftEndsNextDay') : null}
                      {durationLabel ? ` ${t('DurationLabel', { duration: durationLabel })}` : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Break Duration */}
              <Input
                type="number"
                label={t('BreakDurationMinutes')}
                value={formData?.breakDuration ?? ''}
                onChange={(e) => {
                  const v = e?.target?.value;
                  handleInputChange('breakDuration', v === '' ? null : parseInt(v, 10));
                }}
                min="0"
                step="15"
              />

              {/* Break Start/End Time */}
              <div className="grid grid-cols-2 gap-4">
                <TimeField
                  label={t('BreakStartTime', { defaultValue: 'Break Start Time' })}
                  value={formData?.breakStartTime}
                  onChange={(v) => handleInputChange('breakStartTime', v)}
                />
                <TimeField
                  label={t('BreakEndTime', { defaultValue: 'Break End Time' })}
                  value={formData?.breakEndTime}
                  onChange={(v) => handleInputChange('breakEndTime', v)}
                />
              </div>

              {/* User Selection - Multi-select */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('AssignToUsers')} {loadingUsers && <span className="text-xs text-muted-foreground">({t('Loading')})</span>}
                  <span className="text-destructive ms-1">*</span>
                </label>

                {/* Department filter */}
                <div className="mb-2">
                  <Select
                    label={t('Department')}
                    placeholder={t('All departments')}
                    options={departmentOptions.map((dept) => ({
                      label: dept,
                      value: dept,
                    }))}
                    value={selectedDepartment || ''}
                    onChange={(val) => setSelectedDepartment(val || '')}
                    disabled={loadingUsers}
                    clearable
                  />
                </div>

                {/* Select all checkbox */}
                <div className="mb-2">
                  <Checkbox
                    size="sm"
                    label={t('SelectAll', { defaultValue: 'Select all' })}
                    checked={allUsersSelectedInDepartment}
                    indeterminate={someUsersSelectedInDepartment}
                    onChange={(e) => toggleSelectAllUsersInDepartment(!!e?.target?.checked)}
                    disabled={loadingUsers || usersInSelectedDepartmentIds.length === 0}
                  />
                </div>

                <div className="flex gap-2 items-start">
                  <Select
                    multiple
                    searchable
                    className="flex-1"
                    options={filteredUserOptions.map(u => {
                      const deptLabel = typeof u?.department === 'string' ? u.department : (u?.department?.name || u?.department?.title || '');
                      return {
                        label: `${u.name || u.fullName}${deptLabel ? ` - ${deptLabel}` : ''}`,
                        value: u.id
                      };
                    })}
                    value={(formData?.userIds || []).map(u => u.id)}
                    onChange={(ids) => {
                      const optionMap = new Map(filteredUserOptions.map(u => [u.id, u]));
                      const currentMap = new Map((formData?.userIds || []).map(u => [u.id, u]));
                      const selected = (ids || []).map(id =>
                        optionMap.get(id) || currentMap.get(id)
                      ).filter(Boolean);
                      handleInputChange('userIds', selected);
                    }}
                    placeholder={t('SelectUsers')}
                    loading={loadingUsers}
                    disabled={loadingUsers}
                  />
                  {formData?.userIds?.length > 0 && (
                    <Button variant="ghost" size="icon" className="mt-0.5" onClick={() => handleInputChange('userIds', [])} aria-label={t('ClearUsers')}>
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
                {(!formData?.userIds || formData?.userIds?.length === 0) && <p className="text-xs text-destructive mt-1">{t('AtLeastOneUserIsRequired')}</p>}
                {formData?.userIds?.length > 0 && <p className="text-xs text-muted-foreground mt-1">{t('UsersSelectedCount', { count: formData?.userIds?.length })}</p>}

                {availableUsers.length === 0 && !loadingUsers && (
                  <p className="mt-1 text-xs text-warning">
                    {t('NoUsersAvailable')}
                  </p>
                )}
              </div>


              {/* Recurrence Options (only for recurring type) */}
              {formData?.type === 'recurring' && (
                <div className="p-4 bg-muted rounded-lg space-y-4">
                  <h3 className="font-medium text-foreground flex items-center">
                    <Icon name="Repeat" size={18} className="me-2" />
                    {t('RecurrencePattern')}
                  </h3>

                  {/* Days Selection */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      {t('RepeatOnDays')}
                      <span className="text-destructive ms-1">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {weekDays?.map((day) => (
                        <button
                          key={day?.short}
                          type="button"
                          onClick={() => toggleDay(day?.short)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            formData?.recurrenceRule?.days?.includes(day?.short)
                              ? 'bg-primary text-primary-foreground' :'bg-card border border-border text-muted-foreground hover:bg-muted'
                          }`}
                          title={day?.full}
                        >
                          {day?.short}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Week Interval */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      {t('RepeatEvery', { defaultValue: 'Repeat every' })}
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => handleRecurrenceChange('interval', n)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              formData?.recurrenceRule?.interval === n
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-card border border-border text-muted-foreground hover:bg-muted'
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formData?.recurrenceRule?.interval === 1
                          ? t('WeekSingular', { defaultValue: 'week' })
                          : t('WeekPlural', { defaultValue: 'weeks' })}
                      </span>
                    </div>
                    {formData?.recurrenceRule?.interval > 1 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('AlternatingWeeksDescription', {
                          defaultValue: 'Selected days will repeat every {{count}} weeks',
                          count: formData?.recurrenceRule?.interval,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Preview Summary */}
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium text-foreground mb-2">{t('Summary')}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('Type')}:</span>
                    <span className="font-medium">{formData?.type === 'single' ? t('SingleShift') : t('RecurringShift')}</span>
                  </div>
                  {formData?.date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('Date')}:</span>
                      <span className="font-medium">{formData?.date}</span>
                    </div>
                  )}
                  {formData?.startTime && formData?.endTime && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('Time')}:</span>
                      <span className="font-medium">{formData?.startTime} - {formData?.endTime}</span>
                    </div>
                  )}
                  {formData?.userIds && formData?.userIds?.length > 0 && (
                    <div className="flex justify-between items-start">
                      <span className="text-muted-foreground">{t('AssignedTo')}:</span>
                      <span className="font-medium text-end flex-1 ms-2">
                        {formData?.userIds?.map(u => u?.name || u?.fullName).join(', ')}
                      </span>
                    </div>
                  )}
                  {formData?.type === 'recurring' && formData?.recurrenceRule?.days?.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('Days')}:</span>
                      <span className="font-medium">
                        {formData?.recurrenceRule?.interval > 1
                          ? t('EveryNWeeksOn', {
                              defaultValue: 'Every {{count}} weeks on {{days}}',
                              count: formData?.recurrenceRule?.interval,
                              days: formData?.recurrenceRule?.days?.join(', '),
                            })
                          : formData?.recurrenceRule?.days?.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              form="create-schedule-form"
              disabled={submitting}
              loading={submitting}
            >
              {(() => {
                if (formData?.type === 'recurring' && formData?.createNewTemplate) return t('CreateSchedule');
                const isEditingTemplate = formData?.type === 'recurring' && !formData?.createNewTemplate && !!formData?.templateId;
                const isUpdating = !!editingShift || isEditingTemplate || Number(originalRuleId) > 0;
                return isUpdating ? t('UpdateSchedule') : t('CreateSchedule');
              })()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateScheduleDrawer;